"use client";

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { WifiOff, CloudOff, Cpu, Zap } from "lucide-react";

/**
 * 3D-Bühne im Hintergrund + gestochen scharfer On-Device-Chip im Vordergrund.
 */
export default function Preview() {
  const [offlineActive, setOfflineActive] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setOfflineActive((v) => !v), 2400);
    return () => clearInterval(id);
  }, []);

  return (
    <div
      aria-hidden="true"
      className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-2xl p-4 select-none"
      style={{ perspective: "900px" }}
    >
      <motion.div
        animate={{ scale: [1, 1.25, 1], opacity: [0.45, 0.85, 0.45] }}
        transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
        className="pointer-events-none absolute left-1/2 top-1/2 h-40 w-56 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-tr from-purple-600/30 via-emerald-500/25 to-transparent blur-3xl"
      />

      <div
        className="pointer-events-none absolute inset-x-[-20%] bottom-[-35%] h-[110%] opacity-45 dark:opacity-60"
        style={{
          transform: "rotateX(68deg)",
          maskImage:
            "radial-gradient(ellipse 65% 55% at 50% 50%, black 25%, transparent 80%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 65% 55% at 50% 50%, black 25%, transparent 80%)",
        }}
      >
        <motion.div
          animate={{ backgroundPosition: ["0px 0px", "0px 28px"] }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="h-full w-full bg-[linear-gradient(to_right,rgba(168,85,247,0.3)_1px,transparent_1px),linear-gradient(to_bottom,rgba(168,85,247,0.3)_1px,transparent_1px)] bg-[size:28px_28px]"
        />
      </div>

      {/* Gestochen scharfer Vordergrund */}
      <div className="relative z-10 flex flex-col items-center gap-3 antialiased">
        <motion.div
          animate={{
            opacity: offlineActive ? 0.5 : 0.8,
            y: offlineActive ? 2 : 0,
          }}
          className="flex items-center gap-1.5 rounded-full border border-rose-500/40 bg-white/90 px-3 py-1 text-[11px] font-bold text-rose-500 line-through shadow-sm dark:bg-zinc-900/90"
        >
          <CloudOff className="h-3.5 w-3.5" />
          <span>Cloud getrennt</span>
        </motion.div>

        <div className="relative flex items-center justify-center">
          <motion.div
            animate={{ scale: [1, 1.45], opacity: [0.7, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeOut" }}
            className="pointer-events-none absolute h-16 w-16 rounded-2xl border-2 border-emerald-400"
          />

          <motion.div
            animate={{ y: offlineActive ? -3 : 2 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
            className="flex items-center gap-2.5 rounded-2xl border border-emerald-400/80 bg-white px-4 py-2.5 shadow-[0_16px_36px_-6px_rgba(16,185,129,0.55)] dark:bg-zinc-900"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-purple-600 to-emerald-500 text-white shadow-[0_0_16px_rgba(16,185,129,0.7)]">
              <Cpu className="h-4 w-4" />
            </div>
            <div className="flex flex-col">
              <span className="flex items-center gap-1 font-sans text-xs font-black text-neutral-900 dark:text-white">
                <span>100% Lokal</span>
                <Zap className="h-3 w-3 fill-amber-400 text-amber-400" />
              </span>
              <span className="flex items-center gap-1 font-mono text-[10px] font-bold text-emerald-600 dark:text-emerald-400">
                <WifiOff className="h-2.5 w-2.5" />
                Flugmodus OK
              </span>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
